<template>
  <div ref="container" class="mts-modal-form-select">
    <Select
      v-bind="$attrs"
      v-model:value="state"
      :showSearch="showSearch"
      optionFilterProp="label"
      :mode="mode"
      :loading="loading"
      :disabled="disabled"
      :options="getOptions"
      @dropdown-visible-change="handleFetch"
      @change="handleChange"
    >
      <template v-for="item in Object.keys($slots)" #[item]="data">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
      <template #suffixIcon>
        <LoadingOutlined v-if="loading" spin />
        <DownOutlined v-if="!loading && !isOpen" class="!pointer-events-none" />
        <SearchOutlined v-if="!loading && isOpen" />
        <PlusCircleFilled
          :class="['text-14px', { 'text-[#01A887]': !disabled, '!cursor-not-allowed': disabled }]"
          @click="() => !disabled && showAddModal()"
        />
      </template>
      <template v-if="loading" #notFoundContent>
        <span>
          <LoadingOutlined spin class="mr-1" />
          {{ t('common.apiSelectNotFound') }}
        </span>
      </template>
    </Select>

    <Form.ItemRest>
      <BasePageModal @register="registerModal" @ok="onOk" />
    </Form.ItemRest>
  </div>
</template>

<script lang="ts" setup>
  import {
    DownOutlined,
    LoadingOutlined,
    PlusCircleFilled,
    SearchOutlined,
  } from '@ant-design/icons-vue';
  import { Form, Select, type SelectProps } from 'ant-design-vue';
  import { get, isEqual } from 'lodash-es';
  import { computed, PropType, ref, shallowRef, unref, watch } from 'vue';
  import type { PageModal } from '@mts/render';
  import type { Fn, Option, Recordable } from '@mts-fe/types';
  import type { SelectValue } from 'ant-design-vue/es/select';
  import { useAttrs, useRuleFormItem } from '@mts-fe/hooks';
  import { isArray, isFunction, omit, propTypes } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import BasePageModal from '@mts/render/components/BasePageModal.vue';
  import { usePageModal } from '@mts/render/hooks/usePageModal';
  import { PADMode } from '@mts/utils/env';

  defineOptions({ name: 'ApiSelectAdd', inheritAttrs: false });

  const props = defineProps({
    value: { type: [Array, Object, String, Number] as PropType<SelectValue> },
    mode: { type: String as PropType<SelectProps['mode']> },
    numberToString: propTypes.bool.def(true),
    api: {
      type: Function as PropType<(arg?: any) => Promise<Option[] | Recordable>>,
      default: null,
    },
    // api params
    params: propTypes.any.def({}),
    // support xxx.xxx.xx
    resultField: propTypes.string.def(''),
    labelField: propTypes.string.def('label'),
    valueField: propTypes.string.def('value'),
    keyField: propTypes.string.def('key'),
    disabled: propTypes.bool.def(false),
    immediate: propTypes.bool.def(true),
    showSearch: propTypes.bool.def(!PADMode),
    alwaysLoad: propTypes.bool.def(false),
    options: {
      type: Array<Option>,
      default: [],
    },
    beforeFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
    afterFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
    modalConfig: {
      type: Object as PropType<PageModal>,
      default: () => null,
    },
  });

  const emit = defineEmits(['options-change', 'change', 'update:value', 'addOk']);

  const optionsRef = shallowRef<Option[]>([]);

  const loading = ref(false);
  // 首次是否加载过了
  const isFirstLoaded = ref(false);
  const emitData = shallowRef<Option[]>([]);

  // Embedded in the form, just use the hook binding to perform form verification
  const [state] = useRuleFormItem(props, 'value', 'change', emitData);

  const getOptions = computed(() => {
    const { labelField, valueField, keyField, numberToString } = props;

    let data = unref(optionsRef).reduce((prev, next: any) => {
      if (next) {
        const value = get(next, valueField);
        prev.push({
          ...omit(next, [labelField, valueField]),
          key: get(next, keyField) || `${get(next, labelField)}@${value}`,
          label: get(next, labelField),
          value: numberToString ? `${value}` : value,
        });
      }
      return prev;
    }, [] as Option[]);
    return data.length > 0 ? data : props.options;
  });

  watch(
    () => state.value,
    (v) => {
      if (props.mode && !v) {
        state.value = [];
      }
      emit('update:value', v);
    },
  );

  watch(
    () => props.params,
    (value, oldValue) => {
      if (isEqual(value, oldValue)) return;
      fetch();
    },
    { deep: true, immediate: props.immediate },
  );

  async function fetch() {
    let { api, beforeFetch, afterFetch, params, resultField } = props;
    if (!api || !isFunction(api) || loading.value) return;
    optionsRef.value = [];
    try {
      loading.value = true;
      let apiParams = { ...params };
      if (beforeFetch && isFunction(beforeFetch)) {
        const res = await beforeFetch(apiParams);
        if (res !== null && typeof res === 'object') {
          apiParams = res;
        }
        if (typeof res === 'boolean' && !res) return;
      }
      let res = await api(apiParams);
      if (afterFetch && isFunction(afterFetch)) {
        res = (await afterFetch(res)) || res;
      }
      isFirstLoaded.value = true;
      if (isArray(res)) {
        optionsRef.value = [...res];
        emitChange();
        return;
      }
      if (resultField) {
        optionsRef.value = get(res, resultField) || [];
      }
      emitChange();
    } catch (error) {
      console.warn(error);
      // reset status
      isFirstLoaded.value = false;
    } finally {
      loading.value = false;
    }
  }

  const isOpen = ref(useAttrs().open || false);
  async function handleFetch(visible: boolean) {
    isOpen.value = visible;
    if (visible) {
      if (props.alwaysLoad || (!props.immediate && !unref(isFirstLoaded))) {
        await fetch();
      }
    }
  }

  function emitChange() {
    emit('options-change', unref(getOptions));
  }

  function handleChange(_, ...args) {
    emitData.value = args;
  }

  const [registerModal, { showModal }] = usePageModal('ApiSelectAdd');
  const showAddModal = () =>
    props.modalConfig
      ? showModal({}, {}, props.modalConfig)
      : console.error('modalConfig is not available', props.modalConfig);

  const onOk = (...args) => {
    const newVal = args[0].id;
    if (newVal) {
      state.value = isArray(state.value) ? [...state.value, newVal] : newVal;
    }

    emit('addOk', ...args);
    fetch();
  };
</script>
